import { useEffect, useMemo, useRef } from 'react';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import { Product, ProductItem } from '~/lib/data-contract/salesforce';
import { SalesforcePromotionCountdown } from '~/lib/salesforce/types';
import { usePromotionCountdown } from '~/shared/hooks/countdown';
import { getPromotionCountdownEndTime } from '~/shared/utils/get-promotion-countdown-end-time';
import { trustedTime } from '~/shared/utils/trusted-time';

import { useEvents } from '~/shared/hooks/useEvents';
import { _transformProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { QuantityChangeType, useBasket } from '../../hooks/useBasket';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import { InputPlusMinus } from './components/InputPlusMinus';
import {
    StyleMainList,
    StyledProductInfo,
    StyledProductItem,
    StyledInputStepper,
    StyledRemoveProduct,
    StyledRemoveProductButton,
    StyledRemoveButtonWrapper,
} from './styled';
import { ProductImage } from './components/ProductImage';
import { ProductDetails } from './components/ProductDetails';
import { ProductPricePerItem } from './components/ProductPricePerItem';
import { ProductFinalPrice } from './components/ProductFinalPrice';
import { BasketItemPromotionCountdown } from './components/BasketItemPromotionCountdown';
import { useCheckout } from '~/features/checkout/hooks/useCheckout';
import { FreeShippingNotice } from '../FreeShippingNotice';
import Typography from '~/shared/components/Typography/Typography';

type Props = {
    membership: boolean;
};

/**
 * The countdowns the basket's lines still carry although they have ended - e.g. on the product
 * page before coming here - so the lines are priced from before they ended. A key, or null if none.
 */
const getEndedCountdownsKey = (now: number, items?: ProductItem[]) => {
    const ended = (items ?? [])
        .flatMap((item) => item.c_promotionCountdown ?? [])
        .filter((promotion) => {
            const endTime = getPromotionCountdownEndTime(promotion);

            return endTime !== null && endTime <= now;
        })
        .map(({ id, countdown }) => `${id}@${countdown.countdownEndDate}`);

    return ended.length ? ended.sort().join(',') : null;
};

export const BasketList = ({ membership }: Props) => {
    const { translate } = useTranslation();
    const { products } = useProducts();
    const basket = useBasket();
    const { data: frame } = useFrame();
    const { viewCartEvent, productClickEvent } = useEvents(frame);
    const isTriggeredRef = useRef(false);
    const { refetchShipmentMethods } = useCheckout();
    const { setShipmentId } = useCheckoutState();

    useEffect(() => {
        refetchShipmentMethods();
    }, [basket.shipments]);

    const handleMinus = basket.handleBasketErrors((product: Product) => {
        return basket.decreaseItemInBasket(product);
    });

    const handlePlus = basket.handleBasketErrors((product: Product) => {
        return basket.increaseItemInBasket(product);
    });

    const handleQuantityChange = basket.handleBasketErrors(
        (newQuantity: number, product: Product) => {
            const { quantity: currentQuantity } = basket?.productItems?.find(
                (p) => p.itemId === product.itemId,
            ) as Product;

            const quantityDifference = Math.abs(currentQuantity - newQuantity);
            const type =
                newQuantity > currentQuantity ? QuantityChangeType.ADD : QuantityChangeType.REMOVE;

            return basket.updateItemInBasket(product, newQuantity, quantityDifference, type);
        },
    );

    const removeItem = basket.handleBasketErrors(async (product: Product) => {
        await basket.removeItemFromBasket(
            product.itemId,
            _transformProductToDataLayer(product),
            product.quantity,
        );
    });

    useEffect(() => {
        if (!products?.length || isTriggeredRef.current) {
            return;
        }

        viewCartEvent(
            products?.map((item, index) =>
                _transformProductToDataLayer(item, item.quantity, index),
            ),
            basket.orderTotal?.toString() ?? '',
        );

        isTriggeredRef.current = true;
    }, [products]);

    const sortedProducts = useMemo(
        () => Array.isArray(products) && products.sort((item1, item2) => item1.index - item2.index),
        [products],
    );

    const promotionCountdowns = useMemo<SalesforcePromotionCountdown[]>(
        () =>
            Array.isArray(products)
                ? products.flatMap((lineItem) => lineItem.c_promotionCountdown ?? [])
                : [],
        [products],
    );

    // Watched across all lines, so one promotion covering several lines recalculates the basket once.
    // Salesforce starts the discount 2 minutes before the end date, so it is in place by then.
    usePromotionCountdown(promotionCountdowns, basket.recalculateBasket);

    // Salesforce flags prices that no longer match a countdown, as reading never recalculates them.
    useEffect(() => {
        if (basket.c_promotionCountdownRecalculationNeeded) basket.recalculateBasket();
    }, [basket.c_promotionCountdownRecalculationNeeded]);

    const handledEndedCountdownsRef = useRef<string | null>(null);

    /*
     * The basket in memory was read before a countdown ended elsewhere, so neither the watcher above
     * nor Salesforce's flag sees it. Recalculated once per ended countdown, so a basket that still
     * carries it cannot set off another.
     */
    useEffect(() => {
        const key = getEndedCountdownsKey(trustedTime.now(), basket.productItems);

        if (!key || key === handledEndedCountdownsRef.current) return;

        handledEndedCountdownsRef.current = key;
        basket.recalculateBasket();
    }, [basket.lastModified]);

    const onProductClick = (product: Product, index: number) => {
        if (!basket.basketId) return;
        productClickEvent(
            basket.basketId,
            'Basket',
            _transformProductToDataLayer(product, 1, index),
        );
    };

    useEffect(() => {
        setShipmentId('');
    }, []);

    return (
        <StyleMainList>
            <>
                <Typography style={{ alignSelf: 'start' }} variant="title-S" as="h1" weight="bold">
                    {translate('basket.mainTitle')}
                </Typography>

                {basket.c_freeShippingAllowed && <FreeShippingNotice />}

                {Array.isArray(sortedProducts) &&
                    sortedProducts.map((lineItem, index) => {
                        const maxAvailableQuantity = lineItem?.c_availableStock || 1;
                        const disableMinusButton = lineItem?.quantity <= 1;
                        const disablePlusButton = lineItem?.quantity >= maxAvailableQuantity;
                        const isPersonalized = !!(
                            lineItem?.c_personalization_details ||
                            lineItem?.c_personalization_metadata
                        );

                        return (
                            <StyledProductItem key={lineItem?.itemId?.toString() ?? index}>
                                {lineItem?.quantity ? (
                                    <>
                                        <ProductImage
                                            index={index}
                                            lineItem={lineItem}
                                            onProductClick={onProductClick}
                                        />
                                        <StyledProductInfo>
                                            <ProductDetails
                                                index={index}
                                                lineItem={lineItem}
                                                onProductClick={onProductClick}
                                            />
                                            <StyledRemoveButtonWrapper>
                                                <StyledRemoveProductButton
                                                    aria-label={`Fjern alle ${lineItem.productName} fra kurven`}
                                                    variant="plain"
                                                    onClick={() => removeItem(lineItem)}
                                                >
                                                    <StyledRemoveProduct />
                                                </StyledRemoveProductButton>
                                            </StyledRemoveButtonWrapper>
                                            <ProductPricePerItem lineItem={lineItem} />

                                            <StyledInputStepper>
                                                <InputPlusMinus
                                                    onChange={(value: number) =>
                                                        handleQuantityChange(value, lineItem)
                                                    }
                                                    maxAvailableQuantity={maxAvailableQuantity}
                                                    disableMinus={disableMinusButton}
                                                    disablePlus={disablePlusButton}
                                                    handleMinus={() => handleMinus(lineItem)}
                                                    handlePlus={() => handlePlus(lineItem)}
                                                    defaultValue={lineItem.quantity}
                                                    readOnly={isPersonalized}
                                                    productName={lineItem.productName}
                                                />
                                            </StyledInputStepper>

                                            <ProductFinalPrice
                                                lineItem={lineItem}
                                                membership={membership}
                                            />

                                            <BasketItemPromotionCountdown
                                                promotions={lineItem.c_promotionCountdown}
                                            />
                                        </StyledProductInfo>
                                    </>
                                ) : (
                                    ''
                                )}
                            </StyledProductItem>
                        );
                    })}
            </>
        </StyleMainList>
    );
};
